import type { CSSProperties, ReactNode } from 'react';
import { cx } from './cx';
import s from './ui.module.scss';

/**
 * L'etiquette d'un joueur : sa couleur, son nom, et ce qu'on veut dire de lui.
 *
 * La couleur vient du RANG A LA TABLE, pas d'un hachage du nom : elle reste
 * donc la meme du salon au podium, et deux joueurs ne peuvent pas heriter de
 * la meme teinte par malchance.
 *
 * Les bots sont marques. Pas pour les denigrer — ils remplissent la table et
 * font le mode solo — mais parce que finir troisieme derriere deux humains ou
 * derriere deux bots n'est pas la meme partie.
 */
export interface SeatProps {
  name: string;
  seatIndex: number;
  /** Couleur choisie par le joueur (1-8). Absente : deduite de `seatIndex`. */
  colorId?: number | undefined;
  bot?: boolean;
  you?: boolean;
  offline?: boolean;
  /** Etat de la manche, quand il y en a un a montrer. */
  status?: 'thinking' | 'correct' | 'wrong' | 'timeout';
  right?: ReactNode;
  // `| undefined` explicite : sous `exactOptionalPropertyTypes`, une classe de
  // module CSS vaut `string | undefined` et ne passerait pas un `?: string`.
  className?: string | undefined;
}

const STATUS_GLYPH: Record<NonNullable<SeatProps['status']>, string> = {
  thinking: '...',
  correct: 'OK',
  wrong: 'X',
  timeout: '--',
};

export function Seat({
  name,
  seatIndex,
  colorId,
  bot = false,
  you = false,
  offline = false,
  status,
  right,
  className,
}: SeatProps) {
  const seat = colorId ?? (seatIndex % 8) + 1;
  return (
    <div
      className={cx(s.seat, you && s.seatYou, offline && s.seatOffline, className)}
      // Huit couleurs, huit places maximum : le modulo ne sert qu'a ne jamais
      // tomber sur une variable inexistante quand aucune couleur n'est choisie.
      style={{ '--seat': `var(--c-seat-${seat})` } as CSSProperties}
    >
      <span className={s.seatDot} aria-hidden="true" />
      <span className={s.seatName}>
        {name}
        {you ? <span className={s.seatYouTag}>toi</span> : null}
        {bot ? <span className={s.seatBotTag}>bot</span> : null}
      </span>

      {status ? (
        <span className={cx(s.seatStatus, s[`status-${status}`])} aria-label={`etat : ${status}`}>
          {STATUS_GLYPH[status]}
        </span>
      ) : null}

      {right ? <span className={s.seatRight}>{right}</span> : null}
    </div>
  );
}
